/** Sample a background image's pixels to derive an adaptive UI color palette. */

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
    img.src = url;
  });
}

function toHex([r, g, b]) {
  const clamp = (c) => Math.max(0, Math.min(255, Math.round(c)));
  const hex = (c) => clamp(c).toString(16).padStart(2, "0");
  return `#${hex(r)}${hex(g)}${hex(b)}`;
}

/**
 * Draws the image onto a small offscreen canvas and returns:
 *  - average: the mean color across all sampled pixels (good for a muted panel tint)
 *  - accent: the most saturated/vivid pixel color found (good for borders/highlights)
 * Returns null if the image can't be read (load failure, or canvas tainted by a
 * cross-origin image without CORS headers) — callers should fall back gracefully.
 */
export async function extractImagePalette(url) {
  if (!url) return null;
  try {
    const img = await loadImage(url);
    const size = 48;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, size, size);
    const { data } = ctx.getImageData(0, 0, size, size);

    let rSum = 0;
    let gSum = 0;
    let bSum = 0;
    let count = 0;
    let accent = null;
    let bestScore = -1;

    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const a = data[i + 3];
      if (a < 200) continue;

      rSum += r;
      gSum += g;
      bSum += b;
      count += 1;

      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      const saturation = max === 0 ? 0 : (max - min) / max;
      const score = saturation * (max / 255);
      if (score > bestScore) {
        bestScore = score;
        accent = [r, g, b];
      }
    }

    if (!count) return null;
    const average = [rSum / count, gSum / count, bSum / count];

    return {
      averageHex: toHex(average),
      accentHex: toHex(accent || average),
    };
  } catch {
    return null;
  }
}
